import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, type ProjectListItem } from '../api';
import { ThemeSwitcher } from '../ui/ThemeSwitcher';
import {
  IconAgent,
  IconAnalytics,
  IconBoard,
  IconChat,
  IconChevron,
  IconCode,
  IconEnv,
  IconFolder,
  IconHome,
  IconIntegrations,
  IconKnowledge,
  IconLogout,
  IconScenario,
  IconSettings,
  IconTestReport,
} from '../ui/Icon';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { NotificationBell } from './NotificationBell';

const COLLAPSE_KEY = 'bp:sidebar-collapsed';
const VIEW_KEY = 'bp:view';
const FOCUS_DOCS_KEY = 'bp:focus-docs';

type AppView = 'dev' | 'common';

type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  badge?: string;
  disabled?: boolean;
  /** Item só aparece na visão "dev" (programação/infra). */
  dev?: boolean;
};

export function AppShell({ children }: { children: ReactNode }) {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // AppShell wraps <Routes>, so useParams won't work here — parse slug from pathname.
  const slug = useMemo(() => {
    const segs = loc.pathname.split('/').filter(Boolean);
    return segs[0] === 'p' && segs[1] ? segs[1] : undefined;
  }, [loc.pathname]);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Modo foco: na tela de Documentos, esconde o chrome do bug-pilot (sidebar/topbar)
  // pra ficar só a base de docs. NÃO liga sozinho (senão o menu some ao abrir e o
  // usuário se perde) — é opcional pelo botão ⤢/☰, e a escolha fica lembrada.
  const isKnowledge = useMemo(() => /\/p\/[^/]+\/knowledge\/?$/.test(loc.pathname), [loc.pathname]);
  const [focusDocs, setFocusDocs] = useState<boolean>(() => {
    try {
      return localStorage.getItem(FOCUS_DOCS_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(FOCUS_DOCS_KEY, focusDocs ? '1' : '0');
    } catch {
      /* best-effort */
    }
  }, [focusDocs]);
  const focus = isKnowledge && focusDocs;

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* best-effort */
    }
  }, [collapsed]);

  // Visão dev (programação/infra) vs comum (só fluxo de IA). Default pelo papel:
  // admin começa em dev, usuário comum em comum. Persistido por usuário.
  const [view, setView] = useState<AppView>(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      if (v === 'dev' || v === 'common') return v;
    } catch {
      /* ignore */
    }
    return me?.globalRole === 'admin' ? 'dev' : 'common';
  });

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      /* best-effort */
    }
  }, [view]);

  // Project context comes from URL when available; fall back to a quick fetch
  // so the breadcrumbs can show the project name even on direct deep-links.
  const [projectName, setProjectName] = useState<string | null>(null);
  useEffect(() => {
    if (!slug) {
      setProjectName(null);
      return;
    }
    let alive = true;
    api
      .listProjects()
      .then(({ projects }) => {
        if (!alive) return;
        const p = projects.find((x: ProjectListItem) => x.slug === slug);
        setProjectName(p?.name ?? slug);
      })
      .catch(() => {
        if (alive) setProjectName(slug);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const sidebarSections = useMemo<{ label?: string; items: NavItem[] }[]>(() => {
    const sections: { label?: string; items: NavItem[] }[] = [];
    // Geral — visível a todos. As telas de admin/conta ficam agrupadas em
    // Configurações (abas internas filtram o que é só de admin).
    sections.push({
      items: [
        { to: '/', label: 'Projetos', icon: <IconHome />, end: true },
        { to: '/skills', label: 'Skills', icon: <IconKnowledge /> },
        { to: '/settings', label: 'Configurações', icon: <IconSettings /> },
      ],
    });

    if (slug) {
      // IA & Conhecimento — fluxo principal, visível nas duas visões.
      sections.push({
        label: projectName ?? slug,
        items: [
          { to: `/p/${slug}/plano`, label: 'Plano do Projeto', icon: <IconBoard /> },
          // Um único ponto de entrada de chat (NEO/Hermes). Os outros modos (Chat com
          // agentes, Conversas) ficam como abas dentro da própria página.
          { to: `/p/${slug}/neo`, label: 'Conversar com a IA', icon: <IconChat /> },
          { to: `/p/${slug}/agents`, label: 'Agentes', icon: <IconAgent /> },
          { to: `/p/${slug}/artifacts`, label: 'Artefatos', icon: <IconKnowledge /> },
          { to: `/p/${slug}/knowledge`, label: 'Documentos', icon: <IconFolder /> },
          { to: `/p/${slug}/integrations`, label: 'Integrações', icon: <IconIntegrations /> },
        ],
      });

      // Desenvolvimento — só na visão dev.
      if (view === 'dev') {
        sections.push({
          label: 'Desenvolvimento',
          items: [
            { to: `/p/${slug}`, label: 'Board', icon: <IconBoard />, end: true },
            { to: `/p/${slug}/code`, label: 'Código', icon: <IconCode /> },
            { to: `/p/${slug}/console`, label: 'Console', icon: <IconCode /> },
            { to: `/p/${slug}/ephemeral`, label: 'Efêmeros', icon: <IconEnv /> },
            { to: `/p/${slug}/environments`, label: 'Ambientes', icon: <IconEnv /> },
            { to: `/p/${slug}/logs`, label: 'Logs', icon: <IconTestReport /> },
            { to: `/p/${slug}/scenarios`, label: 'Cenários', icon: <IconScenario /> },
            { to: `/p/${slug}/test-reports`, label: 'Test reports', icon: <IconTestReport /> },
            { to: `/p/${slug}/analytics`, label: 'Analytics', icon: <IconAnalytics />, badge: 'em breve', disabled: true },
            { to: `/p/${slug}/settings`, label: 'Configurações', icon: <IconSettings /> },
          ],
        });
      }
    }

    return sections;
  }, [slug, projectName, view, me?.globalRole]);

  const crumbs: Crumb[] = useMemo(() => buildCrumbs(loc.pathname, projectName), [loc.pathname, projectName]);

  const initials = (me?.displayName ?? me?.email ?? '?').trim().slice(0, 1).toUpperCase();

  return (
    <div className={`shell ${collapsed ? 'is-collapsed' : ''} ${focus ? 'is-focus' : ''}`}>
      <aside className="shell-sidebar" aria-label="Navegação principal">
        <Link to="/" className="sidebar-brand" title="Neo-Pilots">
          <span className="sidebar-brand-mark">b</span>
          <span className="sidebar-brand-text">Neo-Pilots</span>
        </Link>

        <nav className="sidebar-nav">
          {sidebarSections.map((section, i) => (
            <div key={i}>
              {section.label && (
                <div className="sidebar-section-label" title={section.label}>
                  {section.label}
                </div>
              )}
              {section.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end ?? false}
                  className={({ isActive }) => {
                    const list = ['sidebar-link'];
                    if (isActive) list.push('is-active');
                    if (it.disabled) list.push('is-disabled');
                    return list.join(' ');
                  }}
                  title={it.label}
                  aria-disabled={it.disabled ?? false}
                  onClick={(e) => {
                    if (it.disabled) e.preventDefault();
                  }}
                >
                  <span className="sidebar-link-icon">{it.icon}</span>
                  <span className="sidebar-link-label">{it.label}</span>
                  {it.badge && <span className="sidebar-link-badge">{it.badge}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="view-switch" role="group" aria-label="Visão">
            <button
              type="button"
              className={`view-switch-btn ${view === 'common' ? 'is-active' : ''}`}
              onClick={() => setView('common')}
              title="Visão simples: só o fluxo de IA"
            >
              Simples
            </button>
            <button
              type="button"
              className={`view-switch-btn ${view === 'dev' ? 'is-active' : ''}`}
              onClick={() => setView('dev')}
              title="Visão dev: board, infra, docker e configurações"
            >
              Dev
            </button>
          </div>
          <ThemeSwitcher />
          {me && (
            <div className="sidebar-user">
              <div className="sidebar-user-avatar" aria-hidden>
                {initials}
              </div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name" title={me.displayName ?? me.email}>
                  {me.displayName ?? me.email}
                </div>
                <div className="sidebar-user-role">{me.globalRole}</div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={async () => {
                  await logout();
                  nav('/login');
                }}
                title="Sair"
                aria-label="Sair"
              >
                <IconLogout size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>

      <button
        type="button"
        className="sidebar-collapse"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
        title={collapsed ? 'Expandir' : 'Recolher'}
      >
        <IconChevron size={14} />
      </button>

      <header className="shell-topbar">
        <Breadcrumbs crumbs={crumbs} />
        <NotificationBell />
      </header>

      <main className="shell-main">{children}</main>

      {isKnowledge && (
        <button
          type="button"
          className="docs-focus-toggle"
          onClick={() => setFocusDocs((v) => !v)}
          title={focus ? 'Mostrar o menu do Neo-Pilots' : 'Modo foco — só os documentos'}
        >
          {focus ? '☰' : '⤢'}
        </button>
      )}
    </div>
  );
}

function buildCrumbs(pathname: string, projectName: string | null): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const out: Crumb[] = [{ label: 'Projetos', to: '/' }];

  if (segments[0] === 'p' && segments[1]) {
    const slug = segments[1];
    out.push({ label: projectName ?? slug, to: `/p/${slug}` });
    if (segments[2] === 'r' && segments[3]) {
      out.push({ label: `Report ${segments[3].slice(0, 8)}…` });
    } else if (segments[2] === 'chat' && segments[3]) {
      out.push({ label: 'Chat', to: `/p/${slug}/chat` });
      out.push({ label: 'Conversa' });
    } else if (segments[2]) {
      const map: Record<string, string> = {
        settings: 'Configurações',
        environments: 'Ambientes',
        ephemeral: 'Efêmeros',
        logs: 'Logs',
        'test-reports': 'Test reports',
        scenarios: 'Cenários',
        chat: 'Chat',
        conversations: 'Conversas',
        code: 'Código',
        agents: 'Agentes',
        integrations: 'Integrações',
        knowledge: 'Base do projeto',
        analytics: 'Analytics',
      };
      out.push({ label: map[segments[2]] ?? segments[2] });
    }
    // No segments[2] = root project = Board (já temos crumb do projeto, não duplica).
  }

  return out;
}
